import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import alocacaoService from '../services/alocacao.service';
import feriadoService  from '../services/feriado.service';

moment.locale('pt-br');
const localizer = momentLocalizer(moment);
// Versão do Calendar com suporte a drag-and-drop e resize
const DnDCalendar = withDragAndDrop(Calendar);

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  verde:   '#1D9E75', verdeBg: '#f0fff4',
  vermelho:'#e53935', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  borda:   '#e8eaed', cinzaBg: '#fafafa',
  texto:   '#1a1a2e', muted:   '#888',
  branco:  '#fff',
  // Cores específicas dos blocos do calendário
  esteProjeto:    { bg: '#E1F5EE', borda: '#0F6E56', texto: '#085041' }, // verde
  outroProjeto:   { bg: '#FAECE7', borda: '#993C1D', texto: '#712B13' }, // coral
  outlook:        { bg: '#E6F1FB', borda: '#185FA5', texto: '#0C447C' }, // azul
  outroTipo:      { bg: '#F3F0FF', borda: '#6B5BB6', texto: '#3D2F87' }, // roxo (FERIAS, SUPORTE...)
  feriadoBg:      '#fef2f2',
};

// CSS de override do react-big-calendar — injeta uma vez quando o componente monta
const RBC_CUSTOM_CSS = `
  .rbc-calendar {
    font-family: inherit;
    font-size: 12px;
  }
  .rbc-toolbar {
    margin-bottom: 12px;
    font-size: 13px;
  }
  .rbc-toolbar button {
    color: #003399;
    border-color: #e8eaed;
    font-weight: 500;
  }
  .rbc-toolbar button.rbc-active {
    background-color: #003399;
    color: #fff;
    border-color: #003399;
  }
  .rbc-toolbar button:hover:not(.rbc-active) {
    background-color: #eff6ff;
  }
  .rbc-header {
    padding: 8px 4px;
    font-size: 11px;
    font-weight: 600;
    color: #555;
    background: #fafafa;
    border-bottom: 1px solid #e8eaed;
  }
  .rbc-time-view {
    border-color: #e8eaed;
  }
  .rbc-time-header-content,
  .rbc-time-content {
    border-color: #e8eaed;
  }
  .rbc-day-slot .rbc-time-slot,
  .rbc-time-slot {
    border-color: #f0f0f0;
  }
  .rbc-event {
    padding: 2px 6px;
    font-size: 11px;
    border-radius: 3px;
    border-left: 3px solid;
    border-top: none;
    border-right: none;
    border-bottom: none;
    transition: opacity 0.15s;
  }
  .rbc-event:hover {
    opacity: 0.85;
  }
  /* Preserva \n no título do evento (usado em blocos de TAREFA com 2 linhas) */
  .rbc-event-content {
    white-space: pre-line;
    line-height: 1.25;
  }
  .rbc-event-label {
    font-size: 10px;
  }
  .rbc-day-bg.dia-feriado {
    background-color: #fef2f2;
  }
  .rbc-today {
    background-color: #fff9e6;
  }
  .rbc-current-time-indicator {
    background-color: #e53935;
    height: 2px;
  }
  .rbc-show-more {
    color: #003399;
    font-weight: 600;
  }
  /* ── Drag-and-drop (Fase 6) ───────────────────────────────────────────
     RBC adiciona a classe .rbc-addons-dnd-resize-ns-anchor nas alças de
     resize (topo/base do bloco). Vamos garantir que arrastáveis tenham
     cursor visualmente correto e que feedback durante drag fique sutil. */
  .rbc-addons-dnd .rbc-event {
    cursor: move;
  }
  .rbc-addons-dnd .rbc-addons-dnd-resize-ns-anchor {
    cursor: ns-resize;
  }
  .rbc-addons-dnd .rbc-addons-dnd-dragged-event {
    opacity: 0.5;
  }
  /* Eventos não-arrastáveis (feriado, férias, etc.) — cursor padrão */
  .rbc-event.evento-nao-arrastavel,
  .rbc-event.evento-nao-arrastavel:hover {
    cursor: default;
  }
  /* ── Violação de capacidade (Fase 7) ─────────────────────────────────
     Bloco gerado com toggle ativo (fora horário, dia não útil ou
     sobreposto). Borda tracejada vermelha + ícone ⚠️ sobreposto. */
  .rbc-event.evento-violacao {
    outline: 2px dashed #dc2626;
    outline-offset: -2px;
    position: relative;
  }
  .rbc-event.evento-violacao::after {
    content: '⚠';
    position: absolute;
    top: 1px; right: 3px;
    font-size: 11px;
    line-height: 1;
    color: #dc2626;
    font-weight: 700;
    pointer-events: none;
  }
`;

// ════════════════════════════════════════════════════════════════════════════
// CalendarioCapacidade — visualização do calendário do analista
// ────────────────────────────────────────────────────────────────────────────
// Props:
//   colaboradorId — id do analista
//   negocioId      — id do projeto atual (pra pintar diferente os blocos
//                    deste projeto vs outros)
//   refreshKey    — força recarregar quando muda
//   onAlocacaoExcluida — callback quando uma alocação é excluída
// ════════════════════════════════════════════════════════════════════════════
function CalendarioCapacidade({
  colaboradorId,
  negocioId,
  refreshKey,
  onAlocacaoExcluida,
}) {
  const [view, setView] = useState('work_week');
  const [date, setDate] = useState(new Date());

  const [alocacoes, setAlocacoes]   = useState([]);
  const [feriados, setFeriados]     = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]             = useState('');

  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  // Toast pra feedback após mover/redimensionar bloco no calendário
  const [toast, setToast] = useState(null); // { tipo: 'ok'|'erro', msg: '...' }
  // Estado pra evitar duplo-drop enquanto a request anterior ainda não voltou
  const [salvandoDnD, setSalvandoDnD] = useState(false);

  // Mostra um toast por 2.5s e some sozinho
  const showToast = useCallback((tipo, msg) => {
    setToast({ tipo, msg });
    setTimeout(() => setToast(null), 2500);
  }, []);

  // Injeta o CSS custom uma vez por mount (não duplica se já existe)
  useEffect(() => {
    const id = 'rbc-portal-alpha-css';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = RBC_CUSTOM_CSS;
      document.head.appendChild(style);
    }
  }, []);

  // ─── Calcula intervalo carregado baseado na view + data atual ───────────
  // Carrega com folga: 1 mês antes/depois da data foco, pra evitar reloads
  // ao navegar com as setas
  const intervalo = useMemo(() => {
    const ini = moment(date).subtract(1, 'month').startOf('week').toDate();
    const fim = moment(date).add(1, 'month').endOf('week').toDate();
    return { ini, fim };
  }, [date]);

  // ─── Carregar alocações do colaborador + feriados do período ────────────
  const carregar = useCallback(async () => {
    if (!colaboradorId) return;
    setCarregando(true);
    setErro('');
    try {
      const [allocs, feriadosListaAnoAtual, feriadosListaAnoSeguinte] = await Promise.all([
        alocacaoService.listarColaborador(colaboradorId, {
          inicio: intervalo.ini.toISOString(),
          fim:    intervalo.fim.toISOString(),
        }),
        feriadoService.listar(intervalo.ini.getFullYear()),
        intervalo.ini.getFullYear() !== intervalo.fim.getFullYear()
          ? feriadoService.listar(intervalo.fim.getFullYear())
          : Promise.resolve([]),
      ]);
      setAlocacoes(allocs || []);
      setFeriados([...(feriadosListaAnoAtual || []), ...(feriadosListaAnoSeguinte || [])]);
    } catch (err) {
      console.error('[CalendarioCapacidade] Erro:', err);
      setErro('Erro ao carregar: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setCarregando(false);
    }
  }, [colaboradorId, intervalo.ini, intervalo.fim]);

  useEffect(() => { carregar(); }, [carregar, refreshKey]);

  // ─── Conjunto de datas ISO que são feriado (pra dayPropGetter) ──────────
  const feriadosSet = useMemo(() => {
    const s = new Set();
    feriados.forEach(f => {
      const iso = new Date(f.data).toISOString().slice(0, 10);
      s.add(iso);
    });
    return s;
  }, [feriados]);

  // ─── Mapeia feriados pra exibir nas células do mês (allDay events) ──────
  const feriadosComoEventos = useMemo(() => {
    return feriados.map(f => {
      const d = new Date(f.data);
      // Usa UTC noon pra evitar problema de timezone no all-day
      const dia = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);
      return {
        id: `feriado-${f.id}`,
        title: `🎌 ${f.descricao}`,
        start: dia,
        end:   dia,
        allDay: true,
        tipoEvento: 'FERIADO',
        feriado: f,
      };
    });
  }, [feriados]);

  // ─── Mapeia alocações pra eventos do react-big-calendar ─────────────────
  const eventosAlocacoes = useMemo(() => {
    return alocacoes.map(a => {
      const ehEsteProjeto = (
        a.tipo === 'PROJETO' &&
        a.referencia_tabela === 'negocios' &&
        a.referencia_id === negocioId
      );

      let categoria;
      let titulo;
      if (ehEsteProjeto) {
        categoria = 'ESTE_PROJETO';
        titulo = `⚡ ${a.horas}h`;
      } else if (a.tipo === 'PROJETO') {
        categoria = 'OUTRO_PROJETO';
        titulo = `📁 ${a.referencia_id || 'Outro projeto'} (${a.horas}h)`;
      } else if (a.tipo === 'FERIAS') {
        categoria = 'OUTRO_TIPO';
        titulo = `🏖️ Férias`;
      } else if (a.tipo === 'SUPORTE') {
        categoria = 'OUTRO_TIPO';
        titulo = `🛠️ Suporte (${a.horas}h)`;
      } else if (a.tipo === 'TAREFA') {
        // Card especial: linha 1 com ícone + título da tarefa,
        // linha 2 com código do projeto + cliente
        categoria = 'OUTRO_TIPO';
        const linha1 = `🏷️ ${a.tarefa_titulo || a.observacao || 'Tarefa'}`;
        const cliente = a.tarefa_cliente_fantasia || a.tarefa_cliente_razao;
        const projeto = a.tarefa_negocio_id;
        let linha2 = '';
        if (projeto && cliente)    linha2 = `${projeto} — ${cliente}`;
        else if (projeto)          linha2 = projeto;
        else if (cliente)          linha2 = cliente;
        titulo = linha2 ? `${linha1}\n${linha2}` : linha1;
      } else if (a.tipo === 'CALL') {
        // r170: call de negócio projetada na agenda
        categoria = 'CALL';
        titulo = `📞 ${a.observacao || 'Call'}`;
      } else {
        categoria = 'OUTRO_TIPO';
        titulo = `${a.tipo} (${a.horas}h)`;
      }

      return {
        id: `aloc-${a.id}`,
        title: titulo,
        start: new Date(a.data_inicio),
        end:   new Date(a.data_fim),
        allDay: false,
        tipoEvento: 'ALOCACAO',
        categoria,
        alocacao: a,
        ehEsteProjeto,
      };
    });
  }, [alocacoes, negocioId]);

  const eventos = useMemo(
    () => [...eventosAlocacoes, ...feriadosComoEventos],
    [eventosAlocacoes, feriadosComoEventos]
  );

  // ─── Customização visual dos eventos ────────────────────────────────────
  const eventPropGetter = useCallback((evento) => {
    if (evento.tipoEvento === 'FERIADO') {
      return {
        className: 'evento-nao-arrastavel',
        style: {
          backgroundColor: COR.vermBg,
          color: COR.vermelho,
          borderLeft: `3px solid ${COR.vermelho}`,
          fontWeight: '600',
        },
      };
    }
    // Alocações que NÃO são PROJETO (férias, suporte, outros tipos) também
    // não são arrastáveis — recebem className pra visual feedback do cursor
    const ehArrastavel = evento.alocacao?.tipo === 'PROJETO';

    let cor;
    switch (evento.categoria) {
      case 'ESTE_PROJETO':  cor = COR.esteProjeto;  break;
      case 'OUTRO_PROJETO': cor = COR.outroProjeto; break;
      case 'OUTLOOK':       cor = COR.outlook;      break;
      case 'CALL':          cor = { bg: '#f3f0ff', texto: '#7c3aed', borda: '#7c3aed' }; break; // r170: call (roxo)
      default:              cor = COR.outroTipo;    break;
    }

    // Detecta violações: alocação foi gerada com toggle ativo
    // (fora horário, dia não útil ou sobreposto). Visual: borda tracejada
    // vermelha em volta + ícone ⚠️ adicionado via ::after no CSS.
    const a = evento.alocacao;
    const temViolacao = !!(a && (a.fora_horario_comercial || a.dia_nao_util || a.sobreposto));

    const classes = [];
    if (!ehArrastavel) classes.push('evento-nao-arrastavel');
    if (temViolacao)   classes.push('evento-violacao');

    return {
      className: classes.join(' '),
      style: {
        backgroundColor: cor.bg,
        color: cor.texto,
        borderLeft: `3px solid ${cor.borda}`,
      },
    };
  }, []);

  // ─── Customização visual das células de dia ─────────────────────────────
  const dayPropGetter = useCallback((data) => {
    const iso = data.toISOString().slice(0, 10);
    if (feriadosSet.has(iso)) {
      return { className: 'dia-feriado' };
    }
    return {};
  }, [feriadosSet]);

  // ─── Click num evento → abre painel de detalhes ─────────────────────────
  const handleSelectEvent = (evento) => {
    setEventoSelecionado(evento);
  };

  const fecharDetalhes = () => setEventoSelecionado(null);

  const excluirAlocacao = async () => {
    if (!eventoSelecionado?.alocacao) return;
    const a = eventoSelecionado.alocacao;
    if (!window.confirm(
      `Excluir essa alocação?\n${formatRange(a.data_inicio, a.data_fim)} (${a.horas}h)`
    )) return;
    setExcluindo(true);
    try {
      await alocacaoService.excluir(a.id);
      setEventoSelecionado(null);
      await carregar();
      if (typeof onAlocacaoExcluida === 'function') {
        onAlocacaoExcluida();
      }
    } catch (err) {
      alert('Erro ao excluir: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setExcluindo(false);
    }
  };

  // ─── Drag-and-drop / Resize ─────────────────────────────────────────────
  // Determina se um evento pode ser arrastado/redimensionado.
  // Regra: alocações de tipo PROJETO (este projeto OU outros) podem ser
  // remanejadas pelo gestor. FÉRIAS, SUPORTE e outros tipos especiais não
  // são tocáveis aqui — quem quer mexer neles vai na origem deles.
  // Feriados (tipoEvento='FERIADO') também não.
  const draggableAccessor = useCallback((evento) => {
    if (evento.tipoEvento !== 'ALOCACAO') return false;
    return evento.alocacao?.tipo === 'PROJETO';
  }, []);

  // Função compartilhada: salva movimento (drop ou resize) no backend
  const salvarMovimento = useCallback(async ({ event, start, end, tipoOp }) => {
    // Trava UI enquanto salva pra evitar duplo-drop
    if (salvandoDnD) return;
    if (!event?.alocacao) return;

    const aloc = event.alocacao;
    // Só PROJETO é editável aqui (defesa em profundidade — draggableAccessor
    // já filtrou, mas se algum bypass acontecer, recusamos no handler também)
    if (aloc.tipo !== 'PROJETO') return;

    // Se o usuário soltou no MESMO lugar (ex: clicou e soltou sem mover),
    // o RBC ainda dispara onEventDrop. Evita request desnecessária.
    const mesmoIni = new Date(aloc.data_inicio).getTime() === new Date(start).getTime();
    const mesmoFim = new Date(aloc.data_fim).getTime()    === new Date(end).getTime();
    if (mesmoIni && mesmoFim) return;

    setSalvandoDnD(true);
    try {
      // Backend espera ISO completo. new Date(...).toISOString() converte UTC,
      // o que pode mudar o dia se o fuso for negativo. Usamos toString local
      // formatada manualmente pra preservar o horário visual.
      const fmt = (d) => {
        const yyyy = d.getFullYear();
        const mm   = String(d.getMonth() + 1).padStart(2, '0');
        const dd   = String(d.getDate()).padStart(2, '0');
        const hh   = String(d.getHours()).padStart(2, '0');
        const mi   = String(d.getMinutes()).padStart(2, '0');
        const ss   = String(d.getSeconds()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
      };
      const r = await alocacaoService.atualizar(aloc.id, {
        data_inicio: fmt(start),
        data_fim:    fmt(end),
      });
      const verbo = tipoOp === 'resize' ? 'redimensionado' : 'movido';
      // Backend pode ter redistribuído horas em outros blocos do mesmo grupo
      // (mesmo analista + mesmo projeto). Mostra resumo se houve.
      const partes = [];
      if (r?.horas != null) partes.push(`${Number(r.horas).toFixed(2)}h`);
      const altCount = (r?.blocos_alterados?.length || 0);
      const apgCount = (r?.blocos_apagados?.length  || 0);
      const crCount  = (r?.blocos_criados?.length   || 0);
      const ajustes = altCount + apgCount + crCount;
      if (ajustes > 0) {
        const det = [];
        if (altCount) det.push(`${altCount} ajustado(s)`);
        if (apgCount) det.push(`${apgCount} apagado(s)`);
        if (crCount)  det.push(`${crCount} novo(s)`);
        partes.push(`redistribuído: ${det.join(', ')}`);
      }
      showToast('ok', `Bloco ${verbo}${partes.length ? ' (' + partes.join(' · ') + ')' : ''}`);
      // Recarrega — mantém o calendário sincronizado com as horas líquidas
      // calculadas pelo backend (almoço descontado etc.) e a redistribuição.
      await carregar();
      if (typeof onAlocacaoExcluida === 'function') {
        onAlocacaoExcluida();
      }
    } catch (err) {
      const msg = err.response?.data?.mensagem || err.message || 'Erro ao salvar.';
      showToast('erro', msg);
    } finally {
      setSalvandoDnD(false);
    }
  }, [salvandoDnD, carregar, onAlocacaoExcluida, showToast]);

  const handleEventDrop = useCallback(({ event, start, end }) => {
    salvarMovimento({ event, start, end, tipoOp: 'drop' });
  }, [salvarMovimento]);

  const handleEventResize = useCallback(({ event, start, end }) => {
    salvarMovimento({ event, start, end, tipoOp: 'resize' });
  }, [salvarMovimento]);

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{ position: 'relative', fontFamily: "'Manrope', sans-serif" }}>
      {erro && (
        <div style={{
          padding: '8px 12px', marginBottom: '10px',
          backgroundColor: COR.vermBg, color: COR.vermelho,
          borderRadius: '5px', fontSize: '12px',
          border: `1px solid ${COR.vermelho}33`,
        }}>{erro}</div>
      )}

      {/* Legenda */}
      <Legenda />

      {/* Calendário */}
      <div style={{ height: '550px', backgroundColor: COR.branco, borderRadius: '6px' }}>
        <DnDCalendar
          localizer={localizer}
          culture="pt-br"
          events={eventos}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          views={['work_week', 'week', 'day', 'month']}
          defaultView="work_week"
          step={30}
          timeslots={2}
          min={new Date(2000, 0, 1, 8, 0)}   // 08:00
          max={new Date(2000, 0, 1, 20, 0)}  // 20:00
          eventPropGetter={eventPropGetter}
          dayPropGetter={dayPropGetter}
          onSelectEvent={handleSelectEvent}
          messages={MENSAGENS_PT}
          formats={FORMATOS}
          // ─── Drag-and-drop (Fase 6) ───────────────────────────────────
          // Só blocos de PROJETO são arrastáveis (este e outros). Feriados,
          // férias, suporte, etc. ficam fixos. resizable={true} permite
          // esticar/encolher pra ajustar a duração do bloco.
          draggableAccessor={draggableAccessor}
          resizableAccessor={draggableAccessor}
          resizable
          onEventDrop={handleEventDrop}
          onEventResize={handleEventResize}
          // dragFromOutsideItem desativado: só arrasta blocos do calendário
        />
      </div>

      {carregando && (
        <div style={{
          position: 'absolute', top: '12px', right: '12px',
          padding: '6px 12px', backgroundColor: COR.azul, color: COR.branco,
          borderRadius: '4px', fontSize: '11px', fontWeight: '600',
          opacity: 0.9, pointerEvents: 'none',
        }}>
          Carregando...
        </div>
      )}

      {/* Indicador discreto durante salvamento de drop/resize */}
      {salvandoDnD && (
        <div style={{
          position: 'absolute', top: '12px', right: '12px',
          padding: '6px 12px', backgroundColor: COR.azul, color: COR.branco,
          borderRadius: '4px', fontSize: '11px', fontWeight: '600',
          opacity: 0.9, pointerEvents: 'none',
        }}>
          Salvando...
        </div>
      )}

      {/* Toast de feedback após drop/resize */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%',
          transform: 'translateX(-50%)',
          padding: '10px 18px', borderRadius: '6px',
          backgroundColor: toast.tipo === 'ok' ? COR.verde : COR.vermelho,
          color: COR.branco, fontSize: '13px', fontWeight: '600',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          fontFamily: "'Manrope', sans-serif",
        }}>
          {toast.tipo === 'ok' ? '✓ ' : '✗ '}{toast.msg}
        </div>
      )}

      {/* Modal de detalhes do evento */}
      {eventoSelecionado && (
        <DetalhesEvento
          evento={eventoSelecionado}
          onFechar={fecharDetalhes}
          onExcluir={excluirAlocacao}
          excluindo={excluindo}
          ehEsteProjeto={eventoSelecionado.ehEsteProjeto}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// LEGENDA
// ════════════════════════════════════════════════════════════════════════════
function Legenda() {
  const itens = [
    { cor: COR.esteProjeto,  label: 'Este projeto' },
    { cor: COR.outroProjeto, label: 'Outros projetos' },
    { cor: COR.outroTipo,    label: 'Outros tipos (férias, suporte...)' },
  ];
  return (
    <div style={{
      display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: '11px',
      marginBottom: '10px', padding: '8px 12px',
      backgroundColor: COR.cinzaBg, borderRadius: '5px',
      border: `1px solid ${COR.borda}`,
    }}>
      {itens.map(it => (
        <span key={it.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{
            display: 'inline-block', width: '12px', height: '10px',
            backgroundColor: it.cor.bg,
            borderLeft: `3px solid ${it.cor.borda}`,
          }} />
          <span style={{ color: COR.muted }}>{it.label}</span>
        </span>
      ))}
      <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <span style={{
          display: 'inline-block', width: '12px', height: '10px',
          backgroundColor: COR.feriadoBg, border: `1px solid ${COR.vermelho}55`,
        }} />
        <span style={{ color: COR.muted }}>Feriado</span>
      </span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DETALHES DO EVENTO (popup lateral simples)
// ════════════════════════════════════════════════════════════════════════════
function DetalhesEvento({ evento, onFechar, onExcluir, excluindo, ehEsteProjeto }) {
  const ehFeriado = evento.tipoEvento === 'FERIADO';

  return (
    <>
      <div onClick={onFechar} style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 10003,
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92%', maxWidth: '420px',
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        zIndex: 10004, overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 18px', borderBottom: `1px solid ${COR.borda}`,
          backgroundColor: COR.azul, color: COR.branco,
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <span style={{ fontSize: '14px', fontWeight: '600', flex: 1 }}>
            Detalhes
          </span>
          <button onClick={onFechar} style={{
            border: 'none', background: 'transparent', color: COR.branco,
            fontSize: '18px', cursor: 'pointer',
          }}>×</button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          {ehFeriado ? (
            <>
              <div style={{ fontSize: '13px', fontWeight: '600', color: COR.vermelho, marginBottom: '4px' }}>
                🎌 {evento.feriado.descricao}
              </div>
              <div style={{ fontSize: '11px', color: COR.muted, marginBottom: '10px' }}>
                {formatarData(evento.feriado.data)}
                {evento.feriado.uf && <> · {evento.feriado.uf}</>}
                <> · {evento.feriado.tipo === 'V' ? 'Variável' : 'Fixo'}</>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: '13px', fontWeight: '600', color: COR.texto, marginBottom: '6px' }}>
                {evento.title}
              </div>

              <Linha label="Período" valor={formatRange(evento.start, evento.end)} />
              <Linha label="Horas líquidas" valor={`${evento.alocacao.horas}h`} />
              <Linha label="Tipo" valor={evento.alocacao.tipo} />
              {evento.alocacao.referencia_id && (
                <Linha label="Referência" valor={`${evento.alocacao.referencia_tabela || '—'} · ${evento.alocacao.referencia_id}`} />
              )}
              <Linha label="Origem" valor={evento.alocacao.origem === 'M' ? 'Manual (gestor moveu)' : 'Automática (motor)'} />
              {(evento.alocacao.fora_horario_comercial ||
                evento.alocacao.dia_nao_util ||
                evento.alocacao.sobreposto) && (
                <Linha label="Flags" valor={
                  [
                    evento.alocacao.fora_horario_comercial && 'Fora horário',
                    evento.alocacao.dia_nao_util            && 'Dia não útil',
                    evento.alocacao.sobreposto              && 'Sobreposto',
                  ].filter(Boolean).join(' · ')
                } />
              )}
              {evento.alocacao.observacao && (
                <Linha label="Observação" valor={evento.alocacao.observacao} />
              )}
            </>
          )}
        </div>

        <div style={{
          padding: '10px 20px', borderTop: `1px solid ${COR.borda}`,
          backgroundColor: COR.cinzaBg,
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
        }}>
          {!ehFeriado && ehEsteProjeto && (
            <button onClick={onExcluir} disabled={excluindo} style={{
              padding: '7px 14px', fontSize: '12px', fontWeight: '600',
              border: `1px solid ${COR.vermelho}`, borderRadius: '5px',
              backgroundColor: COR.vermBg, color: COR.vermelho,
              cursor: excluindo ? 'not-allowed' : 'pointer',
            }}>{excluindo ? 'Excluindo...' : '🗑️ Excluir alocação'}</button>
          )}
          <button onClick={onFechar} style={{
            padding: '7px 14px', fontSize: '12px', fontWeight: '600',
            border: `1px solid ${COR.borda}`, borderRadius: '5px',
            backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
          }}>Fechar</button>
        </div>
      </div>
    </>
  );
}

function Linha({ label, valor }) {
  return (
    <div style={{ marginBottom: '8px', fontSize: '12px' }}>
      <div style={{ color: COR.muted, fontSize: '10px', fontWeight: '600', marginBottom: '2px' }}>
        {label.toUpperCase()}
      </div>
      <div style={{ color: COR.texto }}>{valor}</div>
    </div>
  );
}

// ─── Helpers de formato ──────────────────────────────────────────────────
function formatarData(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

function formatRange(inicio, fim) {
  if (!inicio || !fim) return '—';
  const ini = new Date(inicio);
  const f   = new Date(fim);
  const sameDay = ini.toDateString() === f.toDateString();
  const dataIni = ini.toLocaleDateString('pt-BR');
  const horaIni = ini.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const dataFim = f.toLocaleDateString('pt-BR');
  const horaFim = f.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `${dataIni}, ${horaIni} → ${horaFim}`;
  return `${dataIni} ${horaIni} → ${dataFim} ${horaFim}`;
}

// ─── Mensagens em PT do react-big-calendar ───────────────────────────────
const MENSAGENS_PT = {
  date: 'Data', time: 'Hora', event: 'Evento',
  allDay: 'Dia inteiro', week: 'Semana', work_week: 'Útil',
  day: 'Dia', month: 'Mês', previous: '◀', next: '▶',
  yesterday: 'Ontem', tomorrow: 'Amanhã', today: 'Hoje',
  agenda: 'Agenda',
  noEventsInRange: 'Nenhum evento neste período.',
  showMore: total => `+${total} mais`,
};

const FORMATOS = {
  dayFormat: (d, c, l) => l.format(d, 'ddd DD/MM', c),
  weekdayFormat: (d, c, l) => l.format(d, 'ddd', c),
  dayHeaderFormat: (d, c, l) => l.format(d, 'dddd, DD [de] MMMM', c),
  dayRangeHeaderFormat: ({ start, end }, c, l) =>
    `${l.format(start, 'DD/MM', c)} – ${l.format(end, 'DD/MM/YYYY', c)}`,
  timeGutterFormat: (d, c, l) => l.format(d, 'HH:mm', c),
  eventTimeRangeFormat: ({ start, end }, c, l) =>
    `${l.format(start, 'HH:mm', c)}–${l.format(end, 'HH:mm', c)}`,
};

export default CalendarioCapacidade;
